import { Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MaintenancesApi } from '@core/api/maintenances/maintenances.api';
import { FcSendEmailMessageDialogComponent } from '@core/components/fc-send-email-message-dialog/fc-send-email-message-dialog.component';
import { AddressCoords, Maintenance } from '@core/interfaces';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { CompanyReallocateValidation } from '@validators/company-reallocate-validation';
import * as moment from 'moment';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';


@Component({
  selector: 'dialog-reallocate-maintenance',
  templateUrl: './dialog-reallocate-maintenance.component.html',
  styleUrls: ['./dialog-reallocate-maintenance.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DialogReallocateMaintenanceComponent implements OnDestroy {
  isUpdate: Boolean = false;
  isSaving: Boolean = false;
  refreshDatatable: Boolean = false;
  locationAddressCoords: AddressCoords;
  reallocateMaintenancesForm: FormGroup;

  constructor(
    @Inject(MAT_DIALOG_DATA) public maintenance: Maintenance,
    private readonly matDialog: MatDialog,
    public matDialogRef: MatDialogRef<DialogReallocateMaintenanceComponent>,
    private fuseTranslationLoaderService: FuseTranslationLoaderService,
    private translateService: TranslateService,
    private formBuilder: FormBuilder,
    private maintenancesApi: MaintenancesApi
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });

    this.reallocateMaintenancesForm = this.formBuilder.group({
      id: this.maintenance.id ? this.maintenance.id : null,
      status: this.maintenance.status ? this.maintenance.status : null,
      number: new FormControl({ value: this.maintenance.number, disabled: true }, [Validators.required]),
      type: new FormControl({ value: this.maintenance.maintenanceType && this.maintenance.maintenanceType.name, disabled: true }, [Validators.required]),
      openedDate: new FormControl({ value: moment(this.maintenance.openedAt).toDate(), disabled: true }, [Validators.required]),
      openedTime: new FormControl({ value: moment(this.maintenance.openedAt).format('HH:mm'), disabled: true }, [Validators.required]),
      message: new FormControl(null, [Validators.required]),
      newCompany: new FormControl(null, [Validators.required]),
      company: new FormControl({ value: this.maintenance.company, disabled: true }, [Validators.required]),
      segment: new FormControl({ value: this.maintenance.segment, disabled: true }, [Validators.required]),
      companyScheduleSuggestion: this.maintenance.companyScheduleSuggestion ? this.maintenance.companyScheduleSuggestion : null,
      scheduleSuggestions: this.maintenance.scheduleSuggestions && this.maintenance.scheduleSuggestions.length > 0 ? new FormArray(
        this.maintenance.scheduleSuggestions.map((scheduleSuggestion) => new FormControl(scheduleSuggestion))
      ) : new FormArray([]),
      scheduleSuggestionsStatus: this.maintenance.scheduleSuggestionsStatus ? this.maintenance.scheduleSuggestionsStatus : null
    }, {
      validator: CompanyReallocateValidation.validate
    });

    this.locationAddressCoords = maintenance.location && maintenance.location.address && maintenance.location.address.coords;
  }

  ngOnDestroy(): void {
    this.matDialogRef.close(this.refreshDatatable);
  }

  async reallocateMaintenance(): Promise<void> {
    const result = await this.matDialog.open(FcSendEmailMessageDialogComponent, {
      data: {
        title: this.translateService.instant('DIALOG_REALLOCATE_MAINTENANCE.TITLE'),
        okText: this.translateService.instant('DIALOG_REALLOCATE_MAINTENANCE.OK_TEXT'),
        notOkText: this.translateService.instant('DIALOG_REALLOCATE_MAINTENANCE.NOT_OK_TEXT'),
        cancelText: this.translateService.instant('DIALOG_REALLOCATE_MAINTENANCE.CANCEL_TEXT'),
        message: this.translateService.instant('DIALOG_REALLOCATE_MAINTENANCE.MESSAGE'),
        inputLabel: this.translateService.instant('DIALOG_REALLOCATE_MAINTENANCE.INPUT_LABEL'),
      }
    }).afterClosed()
      .toPromise();

    if (result) {
      this.isSaving = true;
      const { value } = this.reallocateMaintenancesForm;
      const reallocate = {
        message: value.message,
        sendEmail: result.sendEmail,
        sendEmailMessage: result.message || '',
        newCompanyId: value.newCompany && value.newCompany.id,
        scheduleSuggestions: value.scheduleSuggestions.map((scheduleSuggestion) => {
          const date = moment.utc(scheduleSuggestion.date).format('YYYY-MM-DD');
          return { date, time: scheduleSuggestion.time };
        })
      };

      this.maintenancesApi
        .reallocateMaintenance(this.maintenance.id, reallocate)
        .subscribe(() => {
          this.isSaving = false;
          this.refreshDatatable = true;
          this.matDialogRef.close(true);
        });
    }

  }
}
